import buffaloRiverPath from "../data/buffaloRiverPath.json"
import { getAccessPointCoord, getRiverSegmentIndices } from "./tripHelper"

const PADDING = 16
const TARGET_WIDTH = 320

let minLon = Infinity
let maxLon = -Infinity
let minLat = Infinity
let maxLat = -Infinity
buffaloRiverPath.forEach(([lon, lat]) => {
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
})

// Longitude degrees are narrower than latitude degrees away from the equator,
// so they're scaled down here to keep the river's real-world proportions.
const lonScale = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180)
const pixelsPerDegree = TARGET_WIDTH / ((maxLon - minLon) * lonScale)

export const riverSvgViewBox = {
    width: TARGET_WIDTH + PADDING * 2,
    height: (maxLat - minLat) * pixelsPerDegree + PADDING * 2
}

const projectCoordToSvg = (longitude: number, latitude: number): [number, number] => [
    (longitude - minLon) * lonScale * pixelsPerDegree + PADDING,
    (maxLat - latitude) * pixelsPerDegree + PADDING
]

export const riverPathSvgPoints = buffaloRiverPath
    .map(([lon, lat]) => projectCoordToSvg(lon, lat).join(","))
    .join(" ")

export const getProjectedRiverSegmentPoints = (accessPointName1: string, accessPointName2: string): string => {
    const [startIndex, endIndex] = getRiverSegmentIndices(accessPointName1, accessPointName2)
    return buffaloRiverPath
        .slice(startIndex, endIndex + 1)
        .map(([lon, lat]) => projectCoordToSvg(lon, lat).join(","))
        .join(" ")
}

export const getProjectedAccessPointCoord = (accessPointName: string): [number, number] => {
    const coord = getAccessPointCoord(accessPointName)
    return projectCoordToSvg(coord.longitude, coord.latitude)
}

// Wider than PADDING above since it also has to leave room for the Put-In/Take-Out
// text labels drawn next to each access point dot.
const ZOOM_PADDING = 34
const ZOOM_MAX_DIMENSION = 320
// Keeps a tiny/near-straight segment from collapsing to a degenerate (near-zero-area) view.
const ZOOM_MIN_SPAN_DEGREES = 0.006

export interface ZoomedSegmentView {
    viewBoxWidth: number
    viewBoxHeight: number
    pathPoints: string
    accessPoint1: [number, number]
    accessPoint2: [number, number]
}

export const getZoomedSegmentView = (accessPointName1: string, accessPointName2: string): ZoomedSegmentView => {
    const [startIndex, endIndex] = getRiverSegmentIndices(accessPointName1, accessPointName2)
    const segmentPath = buffaloRiverPath.slice(startIndex, endIndex + 1)

    let segMinLon = Infinity
    let segMaxLon = -Infinity
    let segMinLat = Infinity
    let segMaxLat = -Infinity
    segmentPath.forEach(([lon, lat]) => {
        if (lon < segMinLon) segMinLon = lon
        if (lon > segMaxLon) segMaxLon = lon
        if (lat < segMinLat) segMinLat = lat
        if (lat > segMaxLat) segMaxLat = lat
    })

    if (segMaxLon - segMinLon < ZOOM_MIN_SPAN_DEGREES) {
        const centerLon = (segMinLon + segMaxLon) / 2
        segMinLon = centerLon - ZOOM_MIN_SPAN_DEGREES / 2
        segMaxLon = centerLon + ZOOM_MIN_SPAN_DEGREES / 2
    }
    if (segMaxLat - segMinLat < ZOOM_MIN_SPAN_DEGREES) {
        const centerLat = (segMinLat + segMaxLat) / 2
        segMinLat = centerLat - ZOOM_MIN_SPAN_DEGREES / 2
        segMaxLat = centerLat + ZOOM_MIN_SPAN_DEGREES / 2
    }

    const segLonScale = Math.cos(((segMinLat + segMaxLat) / 2) * Math.PI / 180)
    const adjustedLonSpan = (segMaxLon - segMinLon) * segLonScale
    const latSpan = segMaxLat - segMinLat
    // Fits the segment's longer dimension to ZOOM_MAX_DIMENSION so a segment that's
    // taller than it is wide (or vice versa) still lands at a sensible SVG size.
    const scale = ZOOM_MAX_DIMENSION / Math.max(adjustedLonSpan, latSpan)

    const project = (longitude: number, latitude: number): [number, number] => [
        (longitude - segMinLon) * segLonScale * scale + ZOOM_PADDING,
        (segMaxLat - latitude) * scale + ZOOM_PADDING
    ]

    const putInCoord = getAccessPointCoord(accessPointName1)
    const takeOutCoord = getAccessPointCoord(accessPointName2)

    return {
        viewBoxWidth: adjustedLonSpan * scale + ZOOM_PADDING * 2,
        viewBoxHeight: latSpan * scale + ZOOM_PADDING * 2,
        pathPoints: segmentPath.map(([lon, lat]) => project(lon, lat).join(",")).join(" "),
        accessPoint1: project(putInCoord.longitude, putInCoord.latitude),
        accessPoint2: project(takeOutCoord.longitude, takeOutCoord.latitude)
    }
}
